<template>
  <div class="flex flex-row flex-wrap w-10 mt-2">
    <HeadTitle
      :title="
        isEditMode ? 'Editar grupo de nóminas' : 'Detalles del grupo de nóminas'
      "
      description="En esta sección puedes visualizar y editar la información de un grupo de nóminas"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>
  <br />
  <div class="flex flex-column md:w-3 mt-4">
    <label for="client" class="">Compañia</label>
    <InputText id="client" v-model="companyName" class="mt-2" disabled />
  </div>
  <br />
  <Divider />
  <div
    class="flex flex-row flex-wrap w-1 justify-content-between min-h-2 align-items-center"
  >
    <h3>Nóminas</h3>
    <Button
      icon="pi pi-plus"
      severity="secondary"
      v-tooltip.bottom="'Agregar nómina(s)'"
      rounded
      aria-label="Plus"
      v-if="isEditMode"
      @click="openAvailablePayrolls"
    />
  </div>
  <p class="font-light text-sm my-3">
    Agrega o elimina las nóminas que pertenecerán a este grupo de nóminas.
  </p>
  <br />
  <DataTable
    scrollable
    scrollHeight="400px"
    :value="payrolls"
    dataKey="code"
    tableStyle="min-width: 50rem"
    class="lg:w-8 md:w-12"
  >
    <template #empty> No se encontraron nóminas. </template>
    <Column field="code" header="Código"></Column>
    <Column field="name" header="Nombre"></Column>
    <Column header="Plantilla de recibo" sortable>
      <template #body="slotProps">
        <Dropdown
          v-model="slotProps.data.receiptTemplateId"
          :options="receiptTemplates"
          optionLabel="name"
          optionValue="id"
          checkmark
          placeholder="default"
          :disabled="!isEditMode"
        />
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data, field }">
        <ConfirmPopup></ConfirmPopup>
        <Button
          icon="pi pi-trash"
          class="p-button-sm btn-delete"
          severity="danger"
          text
          rounded
          aria-label="Cancel"
          v-if="isEditMode"
          @click="deletePayroll($event, data['code'])"
        />
      </template>
    </Column>
  </DataTable>
  <small id="text-error" class="p-error">{{ payrollsError || '&nbsp;' }}</small>
  <Divider />
  <h3>Configuración</h3>
  <p class="font-light text-sm my-3">
    Configura las opciones disponibles para este grupo de nóminas, como los
    módulos habilitados, la descripción, los días de pago y otras preferencias.
  </p>
  <br />
  <div class="grid px-2 md:w-6 sm:w-12">
    <br />
    <div class="flex flex-column md:col-6 sm:col-12">
      <label for="modules">Módulos</label>
      <MultiSelect
        v-model="selectedModules"
        :filter="isEditMode"
        :options="modules"
        optionLabel="name"
        placeholder="Seleccionar módulos"
        class="w-auto mt-2 mr-5"
        :max-selected-labels="3"
        :optionDisabled="disabledToggle"
        :select-all="isEditMode"
      >
      </MultiSelect>
      <small id="text-error" class="p-error">{{
        moduleError || '&nbsp;'
      }}</small>
    </div>
    <div class="flex flex-column h-5 md:col-6 sm:col-12">
      <label for="description">Descripción</label>
      <Textarea
        class="mt-2"
        style="height: 50px"
        v-model="description"
        :disabled="!isEditMode"
      />
      <small id="text-error" class="p-error">{{
        descriptionError || '&nbsp;'
      }}</small>
    </div>
    <div class="flex flex-column md:col-6 sm:col-12 pr-5">
      <label for="paydays">Días de pago</label>
      <InputText
        id="paydays"
        v-model="paydays"
        class="w-auto mt-2"
        :disabled="!isEditMode"
        placeholder="Ejemplo: 15,30"
      />
      <small id="text-error" class="p-error">{{
        paydaysError || '&nbsp;'
      }}</small>
    </div>
    <div class="flex flex-column">
      <div class="flex flex-row align-items-center mb-3">
        <InputSwitch
          class="mr-2"
          v-model="automatedEmployeeCreation"
          :disabled="!isEditMode"
        />
        <label for="employeesAutomated">Actualizar empleados</label>
        <InfoButton
          message="Al activar esta opción se actualizarán los datos de los empleados de este grupo de nóminas cada hora del día."
        />
      </div>
      <div class="flex flex-row align-items-center mb-3">
        <InputSwitch
          class="mr-2"
          v-model="seeReceiptUntilEndDate"
          :disabled="!isEditMode"
        />
        <label for="seeReceiptsUntilEndDate" class="text-md"
          >Mostrar recibos hasta el fin del periodo</label
        >
        <InfoButton
          message="Ejemplo: fecha inicial: 15-09-2024 - fecha final : 30-09-2024, si existe un comprobante de pago dentro de este periodo, este se muestra a los empleado el mismo dia o posterior a la fecha final del periodo"
        />
      </div>
      <div class="flex flex-row align-items-center mb-3">
        <InputSwitch
          v-model="allowPayedVacations"
          :disabled="!isEditMode"
          class="mr-2"
        />
        <label for="allow-paid-vacations">Permitir vacaciones en dinero</label>
        <InfoButton
          message="Al activar esta opción, los empleados podrán solicitar el pago de sus vacaciones en lugar de tomarlas como tiempo libre."
        />
      </div>

      <div class="flex flex-row align-items-center mb-3">
        <InputSwitch
          v-model="allowNegativePTOBalance"
          :disabled="!isEditMode"
          class="mr-2"
        />
        <label for="allow-paid-vacations"
          >Permitir saldo negativo de vacaciones</label
        >
        <InfoButton
          message="Al activar esta opción, los empleados podrán tener un saldo negativo en su balance de vacaciones y pedir vacaciones incluso si no tienen saldo disponible."
        />
      </div>
    </div>

    <template v-if="showNotificationSection">
      <Divider />
      <h5 class="w-12 text-700">Envio de reportes de solicitudes aprobadas</h5>
      <br />
      <div class="flex flex-column md:w-4 ml-2 mt-4">
        <label for="emailsToNotify">Direccion de correo electronico</label>
        <Textarea
          id="emailsToNotify"
          class="mt-2"
          v-model="emailsToNotify"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
      <div class="flex flex-column md:w-1 mt-4 ml-3">
        <label for="notificationDate">Día del mes</label>
        <InputNumber
          id="notificationDate"
          class="mt-2"
          v-model="notificationDate"
          inputId="minmax-buttons"
          showButtons
          :min="1"
          :max="31"
          :disabled="!isEditMode"
        />
      </div>
    </template>
  </div>
  <Footer>
    <template #left>
      <AuditButton
        :record-ids="[payrollConfigId]"
        :entities="['PayrollsConfig']"
      />
    </template>
    <Button
      :label="isEditModeLabel"
      severity="secondary"
      @click="router.back()"
      class="text-sm font-light mr-2"
    />
    <Button
      class="text-sm font-light"
      v-if="isEditMode"
      label="Guardar"
      @click="updateConfig()"
      severity="primary"
      :disabled="!isValidToUpdate"
    />
  </Footer>
  <Dialog
    v-model:visible="modalVisible"
    modal
    header="Nóminas disponibles"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '641px': '100vw' }"
  >
    <DataTable
      v-model:filters="filters"
      style="width: 90%"
      scrollable
      scrollHeight="400px"
      v-model:selection="payrollsToAdd"
      :value="modalPayrolls"
      dataKey="code"
      tableStyle="min-width: 50rem"
      :globalFilterFields="['code', 'name', 'payrollId', 'isInactive']"
    >
      <template #header>
        <div class="flex justify-content-end">
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Buscar" />
          </span>
        </div>
      </template>
      <template #empty> No se encontraron nóminas. </template>
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      <Column field="code" header="Código"></Column>
      <Column field="name" header="Nombre"></Column>
    </DataTable>
    <template #footer>
      <Button
        label="Cancelar"
        class="text-sm font-ligth"
        icon="pi pi-times"
        @click="modalVisible = false"
        text
      />
      <Button
        label="Agregar"
        class="text-sm font-ligth"
        icon="pi pi-check"
        @click="addPayrolls"
        autofocus
      />
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import Footer from '@/shared/components/Footer.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import {
    getPayrollConfigDetail,
    getPayrollsIntegrationByCompanyId,
    updatePayrollConfig,
  } from '../services/payrollsConfigService'
  import { getModules } from '../../../shared/services/ModuleService'
  import { useRoute } from 'vue-router'
  import PayrollConfigDetail from '../models/PayrollConfiDetail'
  import router from '@/router'
  import { useConfirm } from 'primevue/useconfirm'
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import Payroll from '../models/Payroll'
  import { Module } from '../models/Module'
  import { createPayrolls } from '../services/PayrollService'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import { FilterMatchMode } from 'primevue/api'
  import { getReceiptTemplates } from '../services/ReceiptTemplateService'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'

  const path = useRoute()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const companyName = ref()
  const modules = ref()
  const payrollConfig = ref<PayrollConfigDetail>()
  const modalVisible = ref(false)
  const isEditMode = ref<boolean>(false)
  const modalPayrolls = ref()
  const payrollsToAdd = ref()
  const payrollsPivotArray = ref<Payroll[]>()
  const payrollsFromIntegration = ref() //payrolls not saved yet
  const automatedEmployeeCreation = ref(false)
  const seeReceiptUntilEndDate = ref(false)
  const allowPayedVacations = ref(false)
  const allowNegativePTOBalance = ref(false)

  const descriptionSchema = yup
    .string()
    .required('La descripción del grupo es requerida')
  const modulesSchema = yup
    .array()
    .min(1, 'Al menos un módulo debe ser seleccionado')
    .required('los modulos son requeridos')
  const payrollsSchema = yup
    .array()
    .min(1, 'La configuración debe tener al menos una nómina')
    .required('Las nóminas son requeridas')
  const loadingStore = useLoading()
  const showNotificationSection = ref(false)
  const notificationDateSchema = yup
    .number()
    .required('El dia de notificacion es requerido')
    .min(1, 'El dia de notificacion debe ser mayor a 0')
    .max(30, 'El dia de notificacion debe ser menor a 31')
  const emailsSchema = yup
    .string()
    .test('emails', 'Correo invalido', (value) => {
      if (value) {
        const emails = value.split(',')
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })
  const paydaysSchema = yup
    .string()
    .test('paydays', 'dias de pago invalido', (value) => {
      if (value) {
        const paydays = value.split(',')
        const areValidNumbers = paydays.every(
          (day) =>
            /^\d+$/.test(day.trim()) &&
            parseInt(day.trim(), 10) >= 1 &&
            parseInt(day.trim(), 10) <= 31
        )

        const isValidFormat = /^\d+(,\d+)*$/.test(value)

        return areValidNumbers && isValidFormat
      }
      return true
    })
  const receiptTemplates = ref()
  const receiptTemplateIdDefault = '63321c62-b4e7-4e21-ac35-b19207f65991' //default receipt template id

  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedModules,
    errorMessage: moduleError,
    validate: modValidate,
  } = useField<Module[]>('selectedModules', modulesSchema)
  const {
    value: payrolls,
    errorMessage: payrollsError,
    validate: payValidate,
  } = useField<Payroll[]>('selectedPayrolls', payrollsSchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string>('emailsToNotify', emailsSchema)
  const {
    value: notificationDate,
    errorMessage: notificationDateError,
    validate: notificationDateValidate,
  } = useField<number>('notificationDate', notificationDateSchema)
  const {
    value: paydays,
    errorMessage: paydaysError,
    validate: paydaysValidate,
  } = useField<string>('paydays', paydaysSchema)
  const payrollConfigId = ref()

  onMounted(async () => {
    const { id } = path.params
    payrollConfigId.value = String(id)

    getModules({
      onlyAdminModules: false,
    }).then(
      (response) =>
        (modules.value = response.data.map((m) => ({ id: m.id, name: m.name })))
    )
    getReceiptTemplates().then(
      (response) => (receiptTemplates.value = response)
    )

    loadingStore.setLoadingState()

    const response = await getPayrollConfigDetail(String(id))
    payrollConfig.value = response
    payrolls.value = response.payrolls
    companyName.value = response.company.code + '-' + response.company.name
    description.value = response.description
    selectedModules.value = response.modules
    automatedEmployeeCreation.value = response.automatedEmployeeCreation
    seeReceiptUntilEndDate.value = response.seeReceiptUntilEndDate
    allowPayedVacations.value = response.allowPayedVacations
    allowNegativePTOBalance.value = response.allowNegativePTOBalance
    emailsToNotify.value = response.emailsToNotify?.join(',') || ''
    notificationDate.value = Number(response.notificationDate) || 0
    paydays.value = response.paydays?.join(',') || ''

    const integrationCode = response.client.code

    if (integrationCode === '4') {
      showNotificationSection.value = true
    } else {
      showNotificationSection.value = false
    }

    const payrollsFromIntegrationResponse =
      await getPayrollsIntegrationByCompanyId(
        payrollConfig.value?.company.id || ''
      )
    payrollsFromIntegration.value = payrollsFromIntegrationResponse.data
    loadingStore.dismissLoadingState()
  })

  const updateConfig = async () => {
    loadingStore.setLoadingState()
    const payrollsToCreate = payrolls.value.filter((p) => p.id === undefined) //nominas nuevas
    let payrollsToUpdate = payrolls.value
      .filter((p) => p.id !== undefined) //nominas existentes
      .map((p) => ({
        payrollId: p.id || '',
        receiptTemplateId: p.receiptTemplateId || '',
      }))

    const emailsWithoutLineBreaks = emailsToNotify.value.replace(
      /(\r\n|\n|\r)/gm,
      ''
    )
    const paydaysWithoutLineBreaks = paydays.value.replace(/(\r\n|\n|\r)/gm, '')

    //convertir string de correos y dias de pago a array
    const emails = emailsWithoutLineBreaks.includes(',')
      ? emailsWithoutLineBreaks.split(',')
      : emailsWithoutLineBreaks.trim().length > 0
      ? [emailsWithoutLineBreaks]
      : []

    const paydaysArray = paydaysWithoutLineBreaks.includes(',')
      ? paydaysWithoutLineBreaks.split(',')
      : paydaysWithoutLineBreaks.trim().length > 0
      ? [paydaysWithoutLineBreaks]
      : []

    let updateResp = 'OK'
    let createResp = 'OK'

    if (payrollsToUpdate.length > 0) {
      updateResp = await updatePayrollConfig(
        {
          description: description.value,
          modules: selectedModules.value.map((m) => m.id),
          payrolls: payrollsToUpdate,
          automatedEmployeeCreation: automatedEmployeeCreation.value,
          seeReceiptUntilEndDate: seeReceiptUntilEndDate.value,
          allowPayedVacations: allowPayedVacations.value,
          allowNegativePTOBalance: allowNegativePTOBalance.value,
          emailsToNotify: emails,
          notificationDate: notificationDate.value.toString(),
          paydays: paydaysArray,
        },
        payrollConfig.value?.id || ''
      )
    }

    if (payrollsToCreate.length > 0) {
      createResp = await createPayrolls({
        configId: payrollConfig.value?.id || '',
        payrolls: payrollsToCreate,
      })
    }

    if (updateResp === 'OK' && createResp === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Grupo de nóminas actualizado con exito`,
      })

      loadingStore.dismissLoadingState()
      router.push('/payrollsConfig')
    }
  }

  const disabledToggle = (): boolean => !isEditMode.value

  const deletePayroll = (event: any, code: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-text p-button-danger font-light text-sm',
      rejectClass: 'p-button-primary font-light text-sm',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const payrollDeleted = payrolls.value.find((p: any) => p.code === code)
        //eliminar nomina de la tabla
        payrolls.value = payrolls.value.filter((p: any) => p.code !== code)

        if (payrollDeleted?.id !== undefined) {
          if (payrollsPivotArray.value)
            payrollsPivotArray.value = [
              ...payrollsPivotArray.value,
              payrollDeleted,
            ]
          else payrollsPivotArray.value = [payrollDeleted]
        }
      },
      reject: () => {},
    })
  }

  const addPayrolls = () => {
    payrolls.value = [
      ...payrolls.value,
      ...payrollsToAdd.value.map((p: any) => ({
        ...p,
        receiptTemplateId: receiptTemplateIdDefault || '',
      })),
    ]
    payrollsToAdd.value.forEach((payroll: Payroll) => {
      if (payroll.id !== undefined) {
        payrollsPivotArray.value = payrollsPivotArray.value?.filter(
          (p) => p.code !== payroll.code
        )
      }
    })

    modalPayrolls.value = null
    payrollsToAdd.value = null
    modalVisible.value = false
  }

  const openAvailablePayrolls = async () => {
    modalVisible.value = true
    const availablePayrolls = payrollsFromIntegration.value.filter(
      (pi: any) => !payrolls.value.some((p: any) => isEqual(pi, p))
    )
    if (payrollsPivotArray.value === undefined) {
      modalPayrolls.value = availablePayrolls
    } else {
      modalPayrolls.value = [...payrollsPivotArray.value, ...availablePayrolls]
    }
  }

  const isEqual = (payroll1: any, payroll2: any) => {
    return payroll1.payrollId === payroll2.payrollId ? true : false
  }

  const isEditModeLabel = computed(() =>
    isEditMode.value ? 'Cancelar' : 'Volver'
  )
  const isValidToUpdate = computed(() =>
    description.value &&
    selectedModules.value?.length > 0 &&
    payrolls.value.length > 0
      ? true
      : false
  )

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }
</script>

<style scoped>
  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }

  .p-button-sm {
    width: 10px;
  }
</style>
